import React, { useEffect, useState, Suspense } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Stack,
  Button,
  TextField,
  Autocomplete,
  Alert,
} from "@mui/material";
import MonitorIcon from "@mui/icons-material/Monitor";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import toast from "react-hot-toast";
import _ from "lodash";

import SavedScanDetails from "../../SavedScan/SavedScanDetails";
import DenseTable from "../../components/Tables/Tables";
import BackTestTiles from "../../SavedScan/BackTestTiles";
import SymbolsListTable from "../../SavedScan/SymbolsListTable";
import ReactHotToaster from "../../components/common/ReactHotToaster";
import NewScan from "../../components/main/NewScan";
import BasicButton from "../../components/Button/BasicButton";
import ControlledCheckbox from "../../components/Checkbox/Checkbox";
import SaveScanModal from "../../Screener/SaveScanModal";

import {
  screenerSetOptions,
  screenerSetDefaultValues,
} from "../../utils/screener-set-as";

import {
  optionsShowingInputField,
  optionsShowingMultipleFields,
  optionsShowingTwoInputFields,
  optionsWithoutDefaultInput,
  timeFrameShowingIntraday,
} from "../../utils/Macd";

import axiosConfig from "../../axios/axiosConfig";

const SingleSavedScanPage = () => {
  const { screenerId } = useParams();
  const navigate = useNavigate();

  // --- MAIN STATES ---
  const [isLoading, setIsLoading] = useState(false);
  const [screenerObjDetails, setScreenerObjDetails] = useState({});
  const [savedScreenerResult, setSavedScreenerResult] = useState(null);
  const [latestStocks, setLatestStocks] = useState([]);
  const [userScreenerAlertDetail, setUserScreenerAlertDetail] = useState({});
  const [savedScan, setSavedScan] = useState({});
  const [scanQuery, setScanquery] = useState([]);
  const [selectedStockCategory, setSelectedStockCategory] = useState("");

  // For NewScan UI and filters
  const [displayFilterData, setDisplayFilterData] = useState([]);
  const [combination, setCombination] = useState("pass");
  const [segment, setSegment] = useState("");

  // For Recommendations
  const [screenerSetAs, setScreenerSetAs] = useState(screenerSetDefaultValues);
  const recom_Options = [
    { id: 1, label: "Soft Buy", value: "soft_signal" },
    { id: 2, label: "Buy", value: "moderate_signal" },
    { id: 3, label: "Strong Buy", value: "strong_signal" },
  ];
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [recomInputValue, setRecomInputValue] = useState("");

  // Save/Modal states
  const [openSaveScanModal, setOpenSaveScanModal] = useState(false);
  const [isSaveToDashbaord, setIsSaveToDashbaord] = useState(false);
  const [emptyObj, setEmptyObj] = useState();

  // For scan result
  const [screenerResult, setScreenerResult] = useState(null);
  const [stockList, setStockList] = useState([]);
  const [userAlertList, setUserAlertList] = useState([]);

  // --- BACKTEST states ---
  const [showBackTestTiles, setShowBackTestTiles] = useState(true);
  const [timeframeDate, setTimeframeDate] = useState(null);
  const [symbolsData, setSymbolsData] = useState([]);
  const [isSymbolsDataLoading, setIsSymbolsDataLoading] = useState(false);
  const [backtestCurrentPage, setBacktestCurrentPage] = useState(1);
  const [backtestTotalStocks, setBacktestTotalStocks] = useState(0);

  // --- 1. Fetch scan details on mount ---
  useEffect(() => {
    setIsLoading(true);
    axios
      .get("/tnibroadmin/scanner", {
        params: { id: screenerId },
      })
      .then((response) => {
        if (response.status === 200 || response.statusText === "OK") {
          setScreenerObjDetails(response.data.scanner_data);
          setSavedScreenerResult(response.data);
          setLatestStocks(response?.data?.latest_stock);
          setUserScreenerAlertDetail(
            response.data.scanner_data?.screener_alert?.[0] || {}
          );
          if (!response.data.scanner_data.hide_query) {
            const screenerIdResponse = {
              ...response.data.scanner_data,
              scanner_query: JSON.parse(
                response.data.scanner_data.scanner_query
              ),
            };
            setSavedScan(screenerIdResponse);
            const query = JSON.parse(response.data.scanner_data.scanner_query);
            setScanquery(query);
            setSegment(response.data.scanner_data.segment || "");
          }
        }
      })
      .catch((error) => {
        console.log(error);
        toast.error(error?.response?.data?.response || "Something went wrong");
      })
      .finally(() => setIsLoading(false));
  }, [screenerId]);

  // --- 2. Initialize displayFilterData when scanQuery is loaded ---
  useEffect(() => {
    if (scanQuery.length > 0) {
      setDisplayFilterData(scanQuery);
    }
  }, [scanQuery]);

  // --- 3. Backtest symbols ---
  useEffect(() => {
    if (!timeframeDate || timeframeDate === "" || !screenerId) return;
    setIsSymbolsDataLoading(true);
    axiosConfig({
      method: "post",
      url: "/backtest",
      data: {
        screenerid: screenerId || savedScan?.id,
        timeframedate: timeframeDate,
      },
    })
      .then((res) => {
        if (res.status === 200 || res.statusText === "OK") {
          setBacktestTotalStocks(res.data.total_length);
          setSymbolsData(res.data.response);
        }
      })
      .catch((error) => {
        console.log(error);
        toast.error(error?.response?.data?.response || "Something went wrong");
      })
      .finally(() => setIsSymbolsDataLoading(false));
  }, [timeframeDate, screenerId, savedScan?.id]);

  // --- Handlers for NewScan ---
  const handleFormData = (value, key, id) => {
    const temp = displayFilterData.map((obj, index) => {
      if (index === id) {
        if (
          optionsShowingInputField.indexOf(value) !== -1 ||
          optionsShowingMultipleFields.indexOf(value) !== -1 ||
          optionsWithoutDefaultInput.indexOf(value) !== -1 ||
          optionsShowingTwoInputFields.indexOf(value) !== -1
        ) {
          obj[key] = { indicator: value };
          if (value === "supertrend" || value === "since_day") {
            obj[key] = { ...obj[key], parameter: "close" };
          }
          if (value === "vwap" || value === "obv") {
            obj[key] = { ...obj[key], parameter: "close", ndays: ["0"] };
          }
        }
        if (key === "comparison_operator") {
          obj[key] = { operator: value };
        }
        if (key === "timeframe1" || key === "timeframe2") {
          if (timeFrameShowingIntraday.indexOf(value) !== -1) {
            obj[key] = { ...displayFilterData[key], timeframe_offset: value };
          } else {
            obj[key] = { timeframe_offset: value };
          }
        }
        if (key === "n-timeframe1" || key === "n-timeframe2") {
          const timeFrameKey = key.split("-")[1];
          const nTimeFrameValue = obj[timeFrameKey].timeframe_offset.split("-");
          nTimeFrameValue[0] = value;
          obj[timeFrameKey] = { timeframe_offset: nTimeFrameValue.join("-") };
        }
        if (value === "number") {
          if (key === "measure1") obj.timeframe1 = null;
          if (key === "measure2") obj.timeframe2 = null;
        }
      }
      return obj;
    });
    setDisplayFilterData(temp);
  };

  const handleFormInputValues = (value, key, id, title) => {
    let copiedObject = JSON.parse(JSON.stringify(displayFilterData));
    const temp = copiedObject.map((obj, i) => {
      if (i === id) {
        const parameterCloseArray = [
          "macd-line",
          "macd-signal",
          "macd-histogram",
          "supertrend",
          "ema",
          "rsi",
          "upper-bollinger-band",
          "lower-bollinger-band",
          "sma",
          "wma",
          "hma",
          "sma_atr",
          "rma_atr",
          "cmf",
          "volume_oscillator",
        ];
        if (optionsShowingInputField.indexOf(key) !== -1) {
          if (parameterCloseArray.includes(key)) {
            obj[title].ndays = value?.split(",");
            obj[title].parameter = "close";
          } else {
            obj[title].ndays = value;
            obj[title].parameter = "close";
          }
        }
      }
      return obj;
    });
    setDisplayFilterData(temp);
  };

  const handleEmaValues = (emamsr, emadays, key, id, title) => {
    let copiedObject = JSON.parse(JSON.stringify(displayFilterData));
    const temp = copiedObject.map((obj, i) => {
      if (i === id) {
        if (optionsShowingMultipleFields.indexOf(key) !== -1) {
          obj[title].parameter = emamsr;
          obj[title].ndays = [emadays];
        }
      }
      return obj;
    });
    setDisplayFilterData(temp);
  };

  const handleComparisonGapbetween = (value, key, id) => {
    let copiedObject = JSON.parse(JSON.stringify(displayFilterData));
    const temp = copiedObject.map((obj, index) => {
      if (index === id) {
        if (key === "comparison_operator") {
          obj[key].range = value?.split("-");
        }
      }
      return obj;
    });
    setDisplayFilterData(temp);
  };

  const handleIntradayTimeframe = (value, key, id) => {
    let copiedObject = JSON.parse(JSON.stringify(displayFilterData));
    const temp = copiedObject.map((obj, i) => {
      if (i === id) {
        obj[key].intraday = value;
      }
      return obj;
    });
    setDisplayFilterData(temp);
  };

  const handleScreenerSetAs = (val, key) => {
    setScreenerSetAs({ set_recommended: val });
    if (val) {
      setSelectedRecommendation(recom_Options[0].value);
    } else {
      setSelectedRecommendation(null);
    }
  };

  const handleDuplicateQuery = (id) => {
    const objToDuplicate = displayFilterData.find((Obj, i) => i === id);
    setDisplayFilterData((oldValues) => [...oldValues, { ...objToDuplicate }]);
  };

  const handleDeleteQuery = (id) => {
    const tempArr = [...displayFilterData];
    tempArr.splice(id, 1);
    setDisplayFilterData(tempArr);
  };

  const handleCombinationValue = (val) => setCombination(val);
  const handleSegmentValue = (val) => setSegment(val);

  // -- Save/Publish logic
  const handleUpdateScreener = (editedFields, id) => {
    axios
      .patch(`/tnibroadmin/scanner?id=${id}`, { ...editedFields })
      .then((response) => {
        const scanList = {
          ...response.data.response,
          scanner_query: JSON.parse(response.data.response.scanner_query),
        };
        setSavedScan(scanList);
        const query = JSON.parse(response.data.response.scanner_query);
        setScanquery(query);
        toast.success("Screener details updated!");
      })
      .catch((error) => {
        console.log(error);
        toast.error(error?.response?.data?.response || "Something went wrong");
      });
  };

  const handleSaveScanModal = () => setOpenSaveScanModal(true);

  // -- Save to Dashboard
  const routeToDashboards = () => {
    if (!screenerId) {
      setIsSaveToDashbaord(true);
      setOpenSaveScanModal(true);
      return;
    }
    navigate("/dashboards?openModal=true", {
      state: { screenerId: screenerId },
    });
  };

  // -- Publish/Unpublish
  const handlePublishToApp = () => {
    if (!screenerId) {
      toast.error("Please save the scan first!");
      return;
    }
    let publish = savedScan.publish ? false : true;
    axios
      .patch(`/tnibroadmin/scanner?id=${screenerId}`, { publish })
      .then((response) => {
        toast.success(publish ? "Published to app!" : "Unpublished from App");
        setSavedScan((prev) => ({ ...prev, publish }));
      })
      .catch((error) => {
        console.log(error);
        toast.error(error?.response?.data?.response || "Something went wrong");
      });
  };

  // -- Save Scan logic (uses SaveScanModal)
  const objIsSame = (obj1, obj2) => _.isEqual(obj1, obj2);

  const handleSaveScanScreener = (saved_query_details) => {
    const dataWithFalseDisable = displayFilterData.filter(
      (obj) => obj.disabled === false
    );
    const screenerQueryIsSame = objIsSame(scanQuery, displayFilterData);
    const screenerNameDescIsSame = objIsSame(
      {
        scan_name: savedScan.scan_name,
        scan_description: savedScan.scan_description,
      },
      saved_query_details
    );
    const editedFields = {};
    if (!screenerNameDescIsSame) {
      editedFields.scan_description = saved_query_details.scan_description;
      editedFields.scan_name = saved_query_details.scan_name;
      editedFields.hide_query = saved_query_details?.hide_query;
    }
    if (!screenerQueryIsSame) {
      editedFields.scanner_query = JSON.stringify(dataWithFalseDisable);
    }
    if (screenerId) {
      handleUpdateScreener(editedFields, screenerId);
    } else {
      axios
        .post("/tnibroadmin/scanner", {
          ...saved_query_details,
          segment,
          set_recommended: screenerSetAs.set_recommended,
          scanner_query: JSON.stringify([...dataWithFalseDisable]),
          recommended_signal: screenerSetAs?.set_recommended
            ? selectedRecommendation
            : null,
        })
        .then((response) => {
          if (response.data.status === "success") {
            const scanId = response.data.response.id;
            toast.success("Scan saved!");
            if (isSaveToDashbaord) {
              navigate("/dashboards?openModal=true", {
                state: { screenerId: scanId },
              });
            } else {
              navigate(`/savedscan/${scanId}`);
            }
          }
        })
        .catch((error) => {
          toast.error(
            error?.response?.data?.response || "Something went wrong"
          );
        });
    }
  };

  // -- Run Scan (gets result for display)
  const handleScanSubmit = () => {
    setScreenerResult(null);
    setStockList([]);
    axios
      .post("/screenerapi", {
        segment: segment,
        set_recommended: screenerSetAs.set_recommended,
        filters: displayFilterData,
        // userid: ...  // add userid if needed (from auth)
      })
      .then((response) => {
        setScreenerResult(response.data.response);
        setStockList(response.data.response);
        setUserAlertList(response?.data?.user_alert || []);
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.response ||
            error.message ||
            "Something went wrong"
        );
      });
  };

  // -- Validate empty objects
  const handleIsObjEmpty = (arrayOfObject) => {
    const isEmpty = arrayOfObject?.map((obj) =>
      Object.values(obj).every((value) => {
        if (value === null || value === false) {
          return true;
        }
        return false;
      })
    );
    if (isEmpty) setOpenSaveScanModal(false);
    setEmptyObj(isEmpty[0]);
    return isEmpty[0];
  };

  useEffect(() => {
    handleIsObjEmpty(displayFilterData);
  }, [displayFilterData]);

  // --- RENDER ---
  return (
    <>
      <span>
        <h2>Saved Scan Details</h2>
      </span>
      {screenerObjDetails?.scan_name ? (
        <SavedScanDetails
          screenerId={screenerId || savedScan?.id}
          screenerObjDetails={screenerObjDetails}
          userScreenerAlertDetail={userScreenerAlertDetail}
        />
      ) : (
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      )}

      {/* --- NewScan UI with Button Bar --- */}
      {displayFilterData.length > 0 && (
        <Stack>
          {/* Filter/Query builder */}
          {displayFilterData.map((screenerPayloadObj, index) => (
            <NewScan
              segment={segment}
              handleFormData={handleFormData}
              screenerPayload_DataObj={screenerPayloadObj}
              handleDuplicateData={handleDuplicateQuery}
              handleDeleteForm={handleDeleteQuery}
              handleTextField={handleFormInputValues}
              id={index}
              setDisplayFilterData={setDisplayFilterData}
              handleEmaFields={handleEmaValues}
              handleIntradayTimeframe={handleIntradayTimeframe}
              handleComparisonGapbetween={handleComparisonGapbetween}
            />
          ))}

          <Button
            size="small"
            variant="contained"
            color="warning"
            endIcon={<AddCircleOutlineIcon />}
            onClick={() =>
              setDisplayFilterData((oldValue) => [
                ...oldValue,
                {
                  timeframe1: null,
                  measure1: null,
                  comparison_operator: null,
                  timeframe2: null,
                  measure2: null,
                },
              ])
            }
            sx={{ mb: 2, width: "100%", maxWidth: "80px" }}
          >
            add
          </Button>

          {/* BUTTON BAR --- */}
          <Stack
            direction="row"
            spacing={2}
            gap={1}
            mt={2}
            mb={2}
            flexWrap="wrap"
          >
            <BasicButton
              title="Run Scan"
              callback={handleScanSubmit}
              variant="contained"
              color="success"
            />

            <BasicButton
              title="Save Scan"
              variant="contained"
              callback={handleSaveScanModal}
            />

            <BasicButton
              variant="contained"
              title={
                savedScan.publish ? "UnPublish from App" : "Publish to app"
              }
              callback={handlePublishToApp}
            />

            <BasicButton
              title="save to Dashboard"
              color="secondary"
              variant="contained"
              startIcon={<MonitorIcon />}
              callback={routeToDashboards}
            />

            {/* RECOMMENDED CONTROL */}
            <ControlledCheckbox
              options={screenerSetOptions}
              handleOptionChange={handleScreenerSetAs}
            />

            {/* SOFT BUY LIST */}
            {screenerSetAs.set_recommended && (
              <Autocomplete
                size="small"
                value={
                  recom_Options.find(
                    (opt) => opt.value === selectedRecommendation
                  ) || null
                }
                onChange={(event, newValue) => {
                  setSelectedRecommendation(newValue?.value || "");
                }}
                inputValue={recomInputValue}
                onInputChange={(event, newInputValue) => {
                  setRecomInputValue(newInputValue);
                }}
                id="recommendation-list"
                options={recom_Options}
                sx={{ width: 200 }}
                renderInput={(params) => (
                  <TextField {...params} label="Recommendation" />
                )}
              />
            )}
          </Stack>
        </Stack>
      )}

      {/* TABLE */}
      {isLoading ? (
        <Stack direction="row" spacing={2}>
          <CircularProgress />
        </Stack>
      ) : (
        <DenseTable
          tableData={
            stockList.length ? stockList : savedScreenerResult?.response || []
          }
          userAlertList={userAlertList}
          setUserAlertList={setUserAlertList}
          maxHeight="600px"
          selectedStockCategory={selectedStockCategory}
          setSelectedStockCategory={setSelectedStockCategory}
          latest_stocks={latestStocks}
        />
      )}

      {/* SAVE MODAL */}
      {openSaveScanModal &&
        (emptyObj ? (
          <Stack sx={{ width: "100%" }} spacing={2}>
            <Alert severity="error" onClose={() => setOpenSaveScanModal(false)}>
              Please fill the fields first
            </Alert>
          </Stack>
        ) : (
          <SaveScanModal
            scanFields={{
              scan_name: savedScan.scan_name,
              scan_description: savedScan.scan_description,
            }}
            isOpen={openSaveScanModal && !emptyObj}
            setOpenSaveScanModal={setOpenSaveScanModal}
            handleSaveScanScreener={handleSaveScanScreener}
          />
        ))}

      {/* --- BACKTEST + SYMBOLS TABLE (ORIGINAL FUNCTIONALITY) --- */}
      {isSymbolsDataLoading ? (
        <Stack direction="row" spacing={2}>
          <h1>Backtest Data Loading...</h1>
          <CircularProgress />
        </Stack>
      ) : showBackTestTiles ? (
        <BackTestTiles
          setTimeframeDate={setTimeframeDate}
          timeframeDate={timeframeDate}
          setShowBackTestTiles={setShowBackTestTiles}
          screenerId={savedScan.id ?? screenerId}
        />
      ) : (
        <Suspense fallback={<div>TABLE IS LOADING, please wait...</div>}>
          <SymbolsListTable
            symbolsData={symbolsData}
            setSymbolsData={setSymbolsData}
            setShowBackTestTiles={setShowBackTestTiles}
            timeframeDate={timeframeDate}
            isSymbolsDataLoading={isSymbolsDataLoading}
            totalStocks={backtestTotalStocks}
            currentPage={backtestCurrentPage}
            setCurrentPage={setBacktestCurrentPage}
          />
        </Suspense>
      )}

      <ReactHotToaster />
    </>
  );
};

export default SingleSavedScanPage;
